import { useMemo, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BigTableLayout, {
  useBigTableLayout,
} from 'components/Layout/BigTableLayout';
import BigTable from 'components/Tables/Big';
import { TRow } from 'components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import PageToolbar, {
  PageMainSelector,
  PageSearchInput,
  PageToolbarFiltersButton,
  PageToolbarSection,
  PageToolbarTitle,
} from 'components/Layout/PageToolbar';
import { getAutomationsColumns } from './columns';
import { useSelector } from 'react-redux';
import {
  selectHasFilterError,
  selectAutomationsIsFiltering,
  selectAutomationRowData,
  selectCurrentDirectory,
} from 'store/automationsPage/automations.redux';
import { useClearFilter } from 'hooks/useClearFilter';
import { useFilterErrorEffect } from 'hooks/useFilterErrorEffect';
import { AutomationFilterDropdown } from './AutomationFilterDropdown';
import { ColumnsContext, defaultColumnsConfig } from './columnsContext';
import useField from 'hooks/useField';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { AiExpandableSinglePrompt } from 'ts-components/AiExpandableSinglePrompt';
import {
  FLOW_TYPE,
  TASK_TYPE,
  GUARD_RAIL_CODES,
} from 'ts-components/Conversation';
import { toastVariant, useToast } from 'components/ToastProvider';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { deferExecution } from 'utility/defer';
import { StyledTableWithSidebar } from './AutomationFolders/styles';
import { AutomationFolders } from './AutomationFolders';
import { actions } from './Cells';
import {
  StyledBigTableWrapper,
  StyledButtonWithDropdown,
  StyledCollapse,
} from './styles';
import { FoldersDragAndDropProvider } from './FoldersDragAndDrop/FoldersDragAndDropProvider';
import { DragList } from './FoldersDragAndDrop/DragList';
import { DragTRow } from './FoldersDragAndDrop/DragTRow';

const scrollToTop = () => {
  if (window.scrollTo) {
    // If there's browser support
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } else {
    window.scrollTop = 0;
  }
};

const ColumnsContextProvider = ({ children }) => {
  const pageConfig = useSelector((s) => s.automationPage.pageConfig);
  const [columnsConfig, setColumnsConfig] = useField(
    { ...defaultColumnsConfig, ...pageConfig.columns },
    [pageConfig.columns]
  );
  const [resizing, setResizing] = useState(null);
  return (
    <ColumnsContext.Provider
      value={{ columnsConfig, setColumnsConfig, resizing, setResizing }}
    >
      {children}
    </ColumnsContext.Provider>
  );
};

const DesktopLayout = ({
  groupId = null,
  groups,
  handleChangeGroup,
  handleChangeSearch,
  search = null,
  pageNumber,
  pageSize,
  handleChangePageNumber,
  handleChangePageSize,
  handleChangeDirectory,
  sort,
  handleChangeSort,
  handleUpdateAutomation,
  handleSelectAction,
  openMenu,
  toggleMenu,
  filterInfo,
  applyFiltersAction,
  saveGroupAction,
  deleteGroupAction,
  setFilterName,
  clearFilter,
  addFilterRowHandler,
  errorsReturned,
  canEdit,
  canDelete,
  ...others
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const history = useHistory();
  const isFiltering = useSelector(selectAutomationsIsFiltering);
  const hasFilterError = useSelector(selectHasFilterError);
  const rowData = useSelector(selectAutomationRowData);
  const currentDirectory = useSelector(selectCurrentDirectory);
  const automationsCount = useSelector(
    (s) => s.automationPage.automationsCount
  );
  const containerRef = useRef(null);
  const initialLoad = useRef(true);
  const extractGenerationResult = useCallback((response) => {
    const runMetadata = response.run_metadata;
    const guardrailCode = runMetadata?.guardrail_code;
    const guardrailMessage = runMetadata?.guardrail_message;
    const sessionId = response.session_id || runMetadata?.session_id;
    const generatedAutomation = runMetadata?.automation_payload;

    return {
      hasResult: !!generatedAutomation,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const onGenerationSuccess = useCallback(
    (flowStatusData) => {
      const { run_metadata = {} } = flowStatusData;

      const generatedAutomationUrl =
        flowStatusData.run_metadata?.automation_payload?.url;

      const generatedAutomationId =
        flowStatusData.run_metadata?.automation_payload?.id;

      const guardrailCode = run_metadata?.guardrail_code;
      const guardrailMessage = run_metadata?.guardrail_message;
      const navigateUrl =
        flowStatusData?.next_actions?.[0]?.url || generatedAutomationUrl;
      if (navigateUrl) {
        try {
          const url = new URL(navigateUrl);
          const relativePath = url.pathname + url.search;
          if (guardrailCode && guardrailMessage) {
            showToast({
              message: guardrailMessage,
              variant:
                guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                  ? toastVariant.ALERT
                  : toastVariant.FAILURE,
              autohide: false,
            });
          } else {
            showToast({
              message: t('Agentic Workflow generated successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
          setTimeout(() => {
            history.push(relativePath);
          }, 1500);
          return;
        } catch (error) {
          monitoringExceptionHelper(error, {
            extra: {
              nextActionUrl: navigateUrl,
            },
          });
        }
      }

      if (guardrailCode && guardrailMessage) {
        showToast({
          message: guardrailMessage,
          variant:
            guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
              ? toastVariant.ALERT
              : toastVariant.FAILURE,
          autohide: false,
        });
        if (generatedAutomationId) {
          deferExecution(() => {
            history.push(`/agentic-workflow/${generatedAutomationId}`);
          });
        }
      } else if (generatedAutomationId) {
        showToast({
          message: t('Agentic Workflow generated successfully'),
          variant: toastVariant.SUCCESS,
        });
        setTimeout(() => {
          history.push(`/agentic-workflow/${generatedAutomationId}`);
        }, 1500);
      } else {
        showToast({
          message: t(
            'Agentic Workflow generation failed, please try again later'
          ),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, history, t]
  );

  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          message: usageError[0],
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t(
            'Agentic Workflow generation failed, please try again later'
          ),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t]
  );

  const buttonMenuOptions = useMemo(() => {
    return [
      {
        value: actions.new_automation,
        label: t('New Agentic Workflow'),
      },
      {
        value: actions.new_folder,
        label: t('New Folder'),
      },
    ];
  }, [t]);

  useFilterErrorEffect({ toggleMenu, hasFilterError });

  const { filterDropdownRef, scrollTransitioned, ...bigTablePageProps } =
    useBigTableLayout();
  const { scrolledToTable } = bigTablePageProps;

  const toggle = () => {
    if (scrolledToTable) {
      scrollToTop();
      toggleMenu(true);
    } else {
      toggleMenu((ps) => !ps);
    }
  };

  const automationsHeadData = useMemo(
    () => ({
      meta: {
        sort,
        onSort: (column, direction) => handleChangeSort({ column, direction }),
      },
    }),
    [sort, handleChangeSort]
  );

  const automationsColumns = useMemo(
    () =>
      getAutomationsColumns({
        currentDirectory,
        onUpdateAutomation: handleUpdateAutomation,
        onSelectAction: handleSelectAction,
        onChangeDirectory: handleChangeDirectory,
        canEdit,
        canDelete,
        t,
      }),
    [
      handleUpdateAutomation,
      handleSelectAction,
      handleChangeDirectory,
      currentDirectory,
      canEdit,
      canDelete,
      t,
    ]
  );

  const { aiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.automation_builder
  );
  const displayAiComponent = useMemo(() => {
    if (initialLoad.current && isFiltering) {
      initialLoad.current = false;
      return false;
    }
    return true;
  }, [isFiltering]);

  const handleClearFilter = useClearFilter(clearFilter, toggleMenu);

  return (
    <BigTableLayout
      filtersOpen={openMenu}
      toolbar={
        <PageToolbar>
          <PageToolbarSection>
            <PageMainSelector
              value={groupId || 'none'}
              options={groups}
              onChange={handleChangeGroup}
            />
            <PageToolbarFiltersButton
              badge={filterInfo.numberOfFilters}
              showBackground={openMenu && !scrollTransitioned}
              onClick={toggle}
              errors={hasFilterError}
            />
            <PageSearchInput
              placeholder={t('Find Workflows')}
              value={search || ''}
              onChange={handleChangeSearch}
              loading={isFiltering}
              isClearable
            />
          </PageToolbarSection>
          <PageToolbarTitle
            count={automationsCount}
            single={t('Agentic Workflow')}
            plural={t('Agentic Workflows')}
          />
          <PageToolbarSection>
            <div ref={containerRef} />
            {aiFeaturesEnabled && displayAiComponent && canEdit ? (
              <AiExpandableSinglePrompt
                flowType={FLOW_TYPE.automation_builder_inline}
                taskType={TASK_TYPE.AUTOMATION_PLAN_GENERATION}
                context={{}}
                onOneShotSuccess={onGenerationSuccess}
                onChatSessionSuccess={onGenerationSuccess}
                onGenerationFailure={onGenerationFailure}
                entityName={t('Agentic Workflow')}
                extractGenerationResult={extractGenerationResult}
                messagesConfig={{
                  placeholder: t(
                    "Describe the agentic workflow you'd like to build"
                  ),
                  buildingMessage: t('Building your agentic workflow'),
                  successMessage: t('Agentic Workflow Generated Successfully'),
                  failureMessage: t(
                    'Agentic Workflow Generation Failed, Please try again later'
                  ),
                  expandedPlaceholder: t('Add additional context'),
                  closingMessage: t('Closing session'),
                  initialMessage: t(
                    'What type of agentic workflow would you like to create?'
                  ),
                }}
                importResults={true}
                popoverProps={{
                  container: containerRef.current,
                  placement: 'bottom-end',
                }}
              />
            ) : null}
            {canEdit && (
              <StyledButtonWithDropdown
                onChange={handleSelectAction}
                onClick={() => handleSelectAction({ value: 'new_automation' })}
                options={buttonMenuOptions}
              >
                {t('New Agentic Workflow')}
              </StyledButtonWithDropdown>
            )}
          </PageToolbarSection>
        </PageToolbar>
      }
      filters={
        <StyledCollapse mountOnEnter in={openMenu}>
          <AutomationFilterDropdown
            ref={filterDropdownRef}
            key={filterInfo.key}
            filterData={filterInfo.config}
            groupId={groupId}
            numberOfFilters={filterInfo.numberOfFilters}
            applyFiltersAction={applyFiltersAction}
            saveGroupAction={saveGroupAction}
            deleteGroupAction={deleteGroupAction}
            filterName={filterInfo.name}
            setFilterName={setFilterName}
            clearFilter={handleClearFilter}
            errorsReturned={errorsReturned}
          />
        </StyledCollapse>
      }
      {...bigTablePageProps}
      {...others}
    >
      <FoldersDragAndDropProvider>
        <StyledTableWithSidebar>
          <AutomationFolders
            scrolledToTable={scrolledToTable}
            onSelectAction={handleSelectAction}
            canEdit={canEdit}
            canDelete={canDelete}
          />
          <DragList rowData={rowData} canEdit={canEdit} />
          <StyledBigTableWrapper>
            <ColumnsContextProvider>
              <BigTable
                key={isFiltering}
                data-qa="automation-list"
                scrolledToTable={scrolledToTable}
                columns={automationsColumns}
                head={
                  <TRow
                    head
                    columns={automationsColumns}
                    data={automationsHeadData}
                  />
                }
                staleData={isFiltering}
              >
                {rowData.map((item) => {
                  return (
                    <DragTRow
                      key={item.id}
                      columns={automationsColumns}
                      data={item}
                    />
                  );
                })}
              </BigTable>
            </ColumnsContextProvider>
            <TablePagination
              page={pageNumber}
              perPage={pageSize}
              totalCount={automationsCount}
              onChangePage={handleChangePageNumber}
              onChangePerPage={handleChangePageSize}
            />
          </StyledBigTableWrapper>
        </StyledTableWithSidebar>
      </FoldersDragAndDropProvider>
    </BigTableLayout>
  );
};

export default DesktopLayout;
